import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/** Which V3 tab the slider should open on when `variant === 'v3'`.
 *  The V2 slider ignores this field entirely. */
export type SlidePanelTab = 'profile' | 'resume' | 'ai' | 'chat';

export interface SlidePanelState {
  isOpen: boolean;
  type: 'job' | 'candidate' | null;
  data: any;
  candidateId?: string; // For showing candidate-specific job details
  jobId?: string; // For showing job-specific candidate details
  /** Job context (the job whose applicants/invites the candidate slider was
   *  opened from). Lets the V3 candidate slider show the JOB's pay-rate
   *  range. Optional — non-job callers (billing/contracts) leave it null. */
  jobData?: any;
  /** Candidate context (the candidate whose Applied/Invites tab the JOB
   *  slider was opened from). Lets the V3 job slider show the rate this
   *  candidate was submitted at ("Submitted for"). Optional. */
  candidateData?: any;

  // ── V3 slider extensions ─────────────────────────────────────────────
  /** `'v3'` routes rendering to the new 4-tab sliders
   *  (`<app-candidate-profile-slider>` / `<app-job-profile-slider>`).
   *  Defaults to `'v2'` so existing billing / contracts / work-order
   *  callers that only pass `(data)` / `(data, id)` keep their legacy UI. */
  variant?: 'v2' | 'v3';
  /** Which tab to open on. Eye icon → `'profile'`; match badge → `'ai'`. */
  initialTab?: SlidePanelTab;
  /** Origin surface — used by the AI tab to build the right ranking payload.
   *  Candidate-side values: `'job-applicants' | 'talent-pool' | 'suggested-candidates'`.
   *  Job-side values: `'jobsApplied' | 'suggestedJobs'`. */
  pageFrom?: string;
  /** Hide the AI Insights tab in V3 sliders — set true when opening from
   *  Job Center / Talent Center / Talent Pool, where AI insights are not
   *  applicable. */
  hideAiInsights?: boolean;
  /** Hide the Chat header action + Chat tab in the V3 candidate slider —
   *  set true by Contract Center / Billing Center openers (2026-09-08). */
  hideChat?: boolean;
  /** Single-scroll drawer — set true ONLY by the SuperAdmin org pages
   *  (Job Center / Talent Center / Talent Pool) so the drawer pins to
   *  the viewport and the V3 slider's tab panes own the single scroll.
   *  Drives the .v3-single-scroll class on the drawer host. Other V3
   *  callers leave it false and keep the original whole-panel scroll. */
  compactScroll?: boolean;
}

/** Optional 3rd-arg shape for the V3 open* methods. Everything inside is
 *  optional so the existing 1- and 2-arg call signatures continue to work. */
export interface SlidePanelOpts {
  variant?: 'v2' | 'v3';
  initialTab?: SlidePanelTab;
  pageFrom?: string;
  hideAiInsights?: boolean;
  /** Hide Chat (header action + tab) — see SlidePanelState.hideChat. */
  hideChat?: boolean;
  /** Single-scroll drawer flag — see SlidePanelState.compactScroll. */
  compactScroll?: boolean;
  /** Job context — see SlidePanelState.jobData. Pass the job whose
   *  applicants/invites the candidate slider is opened from so the slider's
   *  Pay Rate shows the job's rate range. */
  jobData?: any;
  /** Candidate context — see SlidePanelState.candidateData. Pass the
   *  candidate whose Applied tab the job slider is opened from so the
   *  slider can show the "Submitted for" rate. */
  candidateData?: any;
}

@Injectable({
  providedIn: 'root'
})
export class SlidePanelService {
  private panelState = new BehaviorSubject<SlidePanelState>({
    isOpen: false,
    type: null,
    data: null,
    candidateId: undefined,
    jobId: undefined,
    variant: 'v2',
    initialTab: undefined,
    pageFrom: undefined,
    hideAiInsights: false,
    hideChat: false
  });

  public panelState$: Observable<SlidePanelState> = this.panelState.asObservable();

  constructor() { }

  openJobDetail(jobData: any, candidateId?: string, opts?: SlidePanelOpts): void {
    this.panelState.next({
      isOpen: true,
      type: 'job',
      data: jobData,
      candidateId: candidateId,
      candidateData: opts?.candidateData ?? null,
      variant: opts?.variant ?? 'v2',
      initialTab: opts?.initialTab,
      pageFrom: opts?.pageFrom,
      hideAiInsights: opts?.hideAiInsights ?? false,
      hideChat: opts?.hideChat ?? false,
      compactScroll: opts?.compactScroll ?? false
    });
  }

  openCandidateDetail(candidateData: any, jobId?: string, opts?: SlidePanelOpts): void {
    this.panelState.next({
      isOpen: true,
      type: 'candidate',
      data: candidateData,
      jobId: jobId,
      jobData: opts?.jobData ?? null,
      variant: opts?.variant ?? 'v2',
      initialTab: opts?.initialTab,
      pageFrom: opts?.pageFrom,
      hideAiInsights: opts?.hideAiInsights ?? false,
      hideChat: opts?.hideChat ?? false,
      compactScroll: opts?.compactScroll ?? false
    });
  }

  close(): void {
    this.panelState.next({
      isOpen: false,
      type: null,
      data: null,
      candidateId: undefined,
      jobId: undefined,
      variant: 'v2',
      initialTab: undefined,
      pageFrom: undefined,
      hideAiInsights: false,
      hideChat: false
    });
  }

  getCurrentState(): SlidePanelState {
    return this.panelState.value;
  }
}
